/**
 * IFRAP-AI.fr - Module Demande de Données
 * 
 * Un outil pour solliciter les administrations avec diplomatie... 
 * puis avec fermeté si nécessaire !
 */

import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { 
  Search, Send, Mail, AlertTriangle, Check, X, RefreshCw,
  Clock, FileText, ExternalLink, Copy, ChevronRight, Megaphone,
  Twitter, Linkedin, Facebook, Newspaper, Scale, Building2, Heart, Sparkles
} from 'lucide-react';

// Statuts visuels - Plus chaleureux avec des emojis
const STATUS_CONFIG = {
  draft: { label: 'Brouillon', emoji: '📝', color: 'bg-slate-500', icon: FileText },
  sent: { label: 'Envoyée', emoji: '✉️', color: 'bg-sky-500', icon: Send },
  reminder_1: { label: '1ère relance', emoji: '⏰', color: 'bg-amber-500', icon: Clock },
  reminder_2: { label: '2ème relance', emoji: '⚠️', color: 'bg-orange-500', icon: AlertTriangle },
  shame: { label: 'Shame & Name', emoji: '📢', color: 'bg-rose-500', icon: Megaphone },
  received: { label: 'Réponse reçue !', emoji: '🎉', color: 'bg-emerald-500', icon: Check },
  partial: { label: 'Réponse partielle', emoji: '🤔', color: 'bg-violet-500', icon: Check },
  refused: { label: 'Refusée', emoji: '😤', color: 'bg-red-600', icon: X },
};

const DataRequestPage = () => {
  // États principaux
  const [activeTab, setActiveTab] = useState('requests'); // requests, scan, new
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // États pour le scan
  const [scanUrl, setScanUrl] = useState('');
  const [scanAdmin, setScanAdmin] = useState('');
  const [scanResults, setScanResults] = useState(null);

  // États pour nouvelle demande
  const [newRequest, setNewRequest] = useState({
    administration_name: '',
    administration_url: '',
    contact_email: '',
    contact_name: '',
    data_requested: [{ name: '', description: '', category: 'autre' }],
    legal_arguments: ['cada', 'crpa_l300'],
  });

  // États pour la vue détaillée
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [shameContent, setShameContent] = useState(null);

  // Arguments juridiques
  const [legalArguments, setLegalArguments] = useState([]);

  useEffect(() => {
    fetchRequests();
    fetchLegalArguments();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/data-request/list');
      setRequests(res.data.requests || []);
      setStats(res.data.stats || {});
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLegalArguments = async () => {
    try {
      const res = await api.get('/api/data-request/legal-arguments');
      setLegalArguments(res.data.arguments || []);
    } catch (err) {
      console.error('Error fetching legal arguments:', err);
    }
  };

  // Scanner une administration
  const handleScan = async () => {
    if (!scanUrl || !scanAdmin) {
      setMessage({ type: 'error', text: 'Remplissez l\'URL et le nom de l\'administration' });
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/data-request/scan-administration', {
        url: scanUrl,
        administration_name: scanAdmin,
        focus_areas: [],
      });
      setScanResults(res.data);
      setMessage({ type: 'success', text: 'Scan terminé' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors du scan' });
    } finally {
      setLoading(false);
    }
  };

  // Créer une demande depuis le scan
  const createRequestFromScan = () => {
    if (!scanResults) return;
    
    const dataToRequest = [
      ...scanResults.missing.map(d => ({ name: d.name, description: `Catégorie: ${d.category}`, category: d.category })),
      ...scanResults.outdated.map(d => ({ name: d.name, description: `Dernière mise à jour: ${d.last_update}`, category: 'statistiques' })),
    ];

    setNewRequest({
      administration_name: scanResults.administration,
      administration_url: scanResults.url,
      contact_email: '',
      contact_name: '',
      data_requested: dataToRequest.length > 0 ? dataToRequest : [{ name: '', description: '', category: 'autre' }],
      legal_arguments: ['cada', 'crpa_l300', 'open_data'],
    });
    setActiveTab('new');
  };

  // Soumettre une nouvelle demande
  const handleCreateRequest = async () => {
    if (!newRequest.administration_name || newRequest.data_requested.length === 0) {
      setMessage({ type: 'error', text: 'Remplissez les champs obligatoires' });
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/data-request/create', newRequest);
      setMessage({ type: 'success', text: `Demande ${res.data.request_id} créée` });
      setActiveTab('requests');
      fetchRequests();
      
      // Reset form
      setNewRequest({
        administration_name: '',
        administration_url: '',
        contact_email: '',
        contact_name: '',
        data_requested: [{ name: '', description: '', category: 'autre' }],
        legal_arguments: ['cada', 'crpa_l300'],
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la création' });
    } finally {
      setLoading(false);
    }
  };

  // Générer un email
  const handleGenerateEmail = async (requestId, level = 'initial') => {
    setLoading(true);
    try {
      const res = await api.post(`/api/data-request/generate-email/${requestId}?reminder_level=${level}`);
      setGeneratedEmail(res.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur génération email' });
    } finally {
      setLoading(false);
    }
  };

  // Marquer comme envoyée
  const handleMarkSent = async (requestId) => {
    try {
      await api.post(`/api/data-request/mark-sent/${requestId}`);
      setMessage({ type: 'success', text: 'Statut mis à jour' });
      fetchRequests();
      setSelectedRequest(null);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur' });
    }
  };

  // Marquer réponse reçue
  const handleResponseReceived = async (requestId, received, partial = false) => {
    try {
      await api.post('/api/data-request/update-response', {
        request_id: requestId,
        response_received: received,
        partial_data: partial,
      });
      setMessage({ type: 'success', text: received ? 'Réponse enregistrée' : 'Statut mis à jour' });
      fetchRequests();
      setSelectedRequest(null);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur' });
    }
  };

  // Générer contenu Shame and Name
  const handleGenerateShame = async (requestId) => {
    setLoading(true);
    try {
      const res = await api.post('/api/data-request/generate-shame-content', {
        request_id: requestId,
        include_chronique: true,
        include_social: true,
        include_article: true,
      });
      setShameContent(res.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur génération shame content' });
    } finally {
      setLoading(false);
    }
  };

  // Ajouter une donnée à la demande
  const addDataItem = () => {
    setNewRequest(prev => ({
      ...prev,
      data_requested: [...prev.data_requested, { name: '', description: '', category: 'autre' }],
    }));
  };

  // Supprimer une donnée de la demande
  const removeDataItem = (index) => {
    setNewRequest(prev => ({
      ...prev,
      data_requested: prev.data_requested.filter((_, i) => i !== index),
    }));
  };

  // Copier dans le presse-papier
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: 'Copié !' });
  };

  // Déterminer le niveau de relance suivant
  const getNextReminderLevel = (status) => {
    const levels = {
      draft: 'initial',
      sent: 'reminder_1',
      reminder_1: 'reminder_2',
      reminder_2: 'shame',
    };
    return levels[status] || 'initial';
  };

  // Rendu des onglets
  const renderTabs = () => (
    <div className="flex gap-2 mb-6">
      {[
        { id: 'requests', label: 'Demandes en cours', count: stats.total },
        { id: 'scan', label: 'Scanner une administration' },
        { id: 'new', label: 'Nouvelle demande' },
      ].map(tab => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === tab.id
              ? 'bg-[#00FF88] text-black'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className="ml-2 px-2 py-0.5 bg-black/20 rounded-full text-xs">{tab.count}</span>
          )}
        </button>
      ))}
    </div>
  );

  // Rendu de la liste des demandes
  const renderRequestsList = () => (
    <div className="space-y-4">
      {/* Stats rapides */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4">
          <p className="text-gray-400 text-sm">Total</p>
          <p className="text-2xl font-mono text-white">{stats.total || 0}</p>
        </div>
        <div className="bg-[#1a1a2e] border border-yellow-500/30 rounded-lg p-4">
          <p className="text-gray-400 text-sm">En attente</p>
          <p className="text-2xl font-mono text-yellow-400">{stats.pending || 0}</p>
        </div>
        <div className="bg-[#1a1a2e] border border-green-500/30 rounded-lg p-4">
          <p className="text-gray-400 text-sm">Reçues</p>
          <p className="text-2xl font-mono text-green-400">{stats.received || 0}</p>
        </div>
        <div className="bg-[#1a1a2e] border border-red-500/30 rounded-lg p-4">
          <p className="text-gray-400 text-sm">Shame candidates</p>
          <p className="text-2xl font-mono text-red-400">{stats.shame_candidates || 0}</p>
        </div>
      </div>

      {/* Liste */}
      {requests.length > 0 ? (
        <div className="space-y-3">
          {requests.map(req => {
            const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.draft;
            const StatusIcon = statusConfig.icon;
            
            return (
              <div
                key={req.id}
                onClick={() => setSelectedRequest(req)}
                className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4 cursor-pointer hover:border-[#00FF88]/50 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-gray-400" />
                    <div>
                      <h4 className="font-medium text-white">{req.administration_name}</h4>
                      <p className="text-sm text-gray-500">
                        {req.data_requested?.length || 0} données demandées • ID: {req.id}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${statusConfig.color} text-white`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusConfig.label}
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          Aucune demande en cours
        </div>
      )}
    </div>
  );

  // Rendu du scanner
  const renderScanner = () => (
    <div className="space-y-6">
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-[#00FF88]" />
          Scanner une administration
        </h3>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm text-gray-400 block mb-1">Nom de l'administration</label>
            <input
              type="text"
              value={scanAdmin}
              onChange={(e) => setScanAdmin(e.target.value)}
              placeholder="Ex: Ministère de l'Économie"
              className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">URL du site</label>
            <input
              type="url"
              value={scanUrl}
              onChange={(e) => setScanUrl(e.target.value)}
              placeholder="https://www.economie.gouv.fr"
              className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white"
            />
          </div>
        </div>

        <button
          onClick={handleScan}
          disabled={loading}
          className="w-full py-3 bg-[#00FF88] text-black font-medium rounded-lg hover:bg-[#00FF88]/90 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
          Scanner le site
        </button>
      </div>

      {/* Résultats du scan */}
      {scanResults && (
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-white">Résultats pour {scanResults.administration}</h4>
          
          {/* Données disponibles */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <h5 className="text-green-400 font-medium mb-2">Données disponibles ({scanResults.available_data?.length || 0})</h5>
            <ul className="space-y-1 text-sm text-gray-300">
              {scanResults.available_data?.map((d, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  {d.name} - {d.format} ({d.last_update})
                </li>
              ))}
            </ul>
          </div>

          {/* Données manquantes */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <h5 className="text-red-400 font-medium mb-2">Données manquantes ({scanResults.missing_data?.length || 0})</h5>
            <ul className="space-y-1 text-sm text-gray-300">
              {scanResults.missing_data?.map((d, i) => (
                <li key={i} className="flex items-center gap-2">
                  <X className="w-4 h-4 text-red-400" />
                  {d.name} - Importance: {d.importance}
                </li>
              ))}
            </ul>
          </div>

          {/* Données obsolètes */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <h5 className="text-yellow-400 font-medium mb-2">Données obsolètes ({scanResults.outdated_data?.length || 0})</h5>
            <ul className="space-y-1 text-sm text-gray-300">
              {scanResults.outdated_data?.map((d, i) => (
                <li key={i} className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  {d.name} - Dernière MàJ: {d.last_update} (devrait être {d.should_be})
                </li>
              ))}
            </ul>
          </div>

          {/* Actions */}
          <button
            onClick={createRequestFromScan}
            className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
          >
            <Mail className="w-5 h-5" />
            Créer une demande pour ces données
          </button>
        </div>
      )}
    </div>
  );

  // Rendu du formulaire nouvelle demande
  const renderNewRequest = () => (
    <div className="space-y-6">
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-4">Nouvelle demande de données</h3>
        
        {/* Infos administration */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm text-gray-400 block mb-1">Administration *</label>
            <input
              type="text"
              value={newRequest.administration_name}
              onChange={(e) => setNewRequest({ ...newRequest, administration_name: e.target.value })}
              placeholder="Nom de l'administration"
              className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">Site web</label>
            <input
              type="url"
              value={newRequest.administration_url}
              onChange={(e) => setNewRequest({ ...newRequest, administration_url: e.target.value })}
              placeholder="https://..."
              className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">Email contact</label>
            <input
              type="email"
              value={newRequest.contact_email}
              onChange={(e) => setNewRequest({ ...newRequest, contact_email: e.target.value })}
              placeholder="contact@administration.gouv.fr"
              className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">Nom du contact</label>
            <input
              type="text"
              value={newRequest.contact_name}
              onChange={(e) => setNewRequest({ ...newRequest, contact_name: e.target.value })}
              placeholder="Madame la Directrice"
              className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white"
            />
          </div>
        </div>

        {/* Données demandées */}
        <div className="mb-4">
          <label className="text-sm text-gray-400 block mb-2">Données demandées *</label>
          {newRequest.data_requested.map((item, idx) => (
            <div key={idx} className="flex gap-2 mb-2">
              <input
                type="text"
                value={item.name}
                onChange={(e) => {
                  const updated = [...newRequest.data_requested];
                  updated[idx].name = e.target.value;
                  setNewRequest({ ...newRequest, data_requested: updated });
                }}
                placeholder="Nom de la donnée"
                className="flex-1 px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
              />
              <input
                type="text"
                value={item.description}
                onChange={(e) => {
                  const updated = [...newRequest.data_requested];
                  updated[idx].description = e.target.value;
                  setNewRequest({ ...newRequest, data_requested: updated });
                }}
                placeholder="Description / justification"
                className="flex-1 px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
              />
              {newRequest.data_requested.length > 1 && (
                <button
                  onClick={() => removeDataItem(idx)}
                  className="px-3 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addDataItem}
            className="text-sm text-[#00FF88] hover:underline"
          >
            + Ajouter une donnée
          </button>
        </div>

        {/* Arguments juridiques */}
        <div className="mb-6">
          <label className="text-sm text-gray-400 block mb-2">Arguments juridiques</label>
          <div className="flex flex-wrap gap-2">
            {legalArguments.map(arg => (
              <button
                key={arg.id}
                onClick={() => {
                  const current = newRequest.legal_arguments;
                  const updated = current.includes(arg.id)
                    ? current.filter(a => a !== arg.id)
                    : [...current, arg.id];
                  setNewRequest({ ...newRequest, legal_arguments: updated });
                }}
                className={`px-3 py-1 rounded text-sm flex items-center gap-1 ${
                  newRequest.legal_arguments.includes(arg.id)
                    ? 'bg-[#00FF88] text-black'
                    : 'bg-gray-700 text-gray-300'
                }`}
              >
                <Scale className="w-3 h-3" />
                {arg.short}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={handleCreateRequest}
          disabled={loading}
          className="w-full py-3 bg-[#00FF88] text-black font-medium rounded-lg hover:bg-[#00FF88]/90 disabled:opacity-50"
        >
          Créer la demande
        </button>
      </div>
    </div>
  );

  // Modal détail demande
  const renderRequestDetail = () => {
    if (!selectedRequest) return null;
    
    const statusConfig = STATUS_CONFIG[selectedRequest.status] || STATUS_CONFIG.draft;
    const nextLevel = getNextReminderLevel(selectedRequest.status);

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-[#1a1a2e] border-b border-gray-700 p-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-white">{selectedRequest.administration_name}</h3>
              <p className="text-sm text-gray-400">ID: {selectedRequest.id}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded text-sm ${statusConfig.color} text-white`}>
                {statusConfig.label}
              </span>
              <button onClick={() => { setSelectedRequest(null); setGeneratedEmail(null); setShameContent(null); }}>
                <X className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Données demandées */}
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-2">Données demandées</h4>
              <ul className="space-y-1">
                {selectedRequest.data_requested?.map((d, i) => (
                  <li key={i} className="text-white text-sm">• {d.name}</li>
                ))}
              </ul>
            </div>

            {/* Actions selon le statut */}
            {!selectedRequest.response_received && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-400">Actions</h4>
                
                {/* Bouton générer email */}
                <button
                  onClick={() => handleGenerateEmail(selectedRequest.id, nextLevel)}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  {selectedRequest.status === 'draft' ? 'Générer l\'email initial' : `Générer relance (${statusConfig.label})`}
                </button>

                {/* Boutons réponse */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => handleResponseReceived(selectedRequest.id, true, false)}
                    className="py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    Réponse complète
                  </button>
                  <button
                    onClick={() => handleResponseReceived(selectedRequest.id, true, true)}
                    className="py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                  >
                    Réponse partielle
                  </button>
                  <button
                    onClick={() => handleResponseReceived(selectedRequest.id, false)}
                    className="py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                  >
                    Pas de réponse
                  </button>
                </div>

                {/* Shame and Name (si 2ème relance) */}
                {selectedRequest.status === 'reminder_2' && (
                  <button
                    onClick={() => handleGenerateShame(selectedRequest.id)}
                    className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                  >
                    <Megaphone className="w-4 h-4" />
                    Lancer le "Shame and Name"
                  </button>
                )}
              </div>
            )}

            {/* Email généré */}
            {generatedEmail && (
              <div className="bg-black/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-[#00FF88]">Email généré</h4>
                  <button
                    onClick={() => copyToClipboard(generatedEmail.body)}
                    className="text-gray-400 hover:text-white"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-400 mb-2">
                  <strong>Objet:</strong> {generatedEmail.subject}
                </p>
                <pre className="text-sm text-white whitespace-pre-wrap font-mono bg-black/30 p-3 rounded max-h-64 overflow-y-auto">
                  {generatedEmail.body}
                </pre>
                <button
                  onClick={() => handleMarkSent(selectedRequest.id)}
                  className="mt-3 w-full py-2 bg-[#00FF88] text-black rounded-lg font-medium"
                >
                  Marquer comme envoyé
                </button>
              </div>
            )}

            {/* Contenu Shame */}
            {shameContent && (
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-red-400 flex items-center gap-2">
                  <Megaphone className="w-5 h-5" />
                  Contenu "Shame and Name"
                </h4>
                <p className="text-sm text-gray-400">
                  {shameContent.administration} • {shameContent.days_waiting} jours d'attente
                </p>

                {/* Chronique */}
                {shameContent.content?.chronique && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-blue-400 font-medium">Chronique Europe 1 / CNews</h5>
                      <button onClick={() => copyToClipboard(JSON.stringify(shameContent.content.chronique, null, 2))}>
                        <Copy className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                    <p className="text-white text-sm mb-2"><strong>Attaque:</strong> {shameContent.content.chronique.attaque}</p>
                    <p className="text-gray-300 text-sm">{shameContent.content.chronique.developpement}</p>
                  </div>
                )}

                {/* Réseaux sociaux */}
                {shameContent.content?.social && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <h5 className="text-purple-400 font-medium mb-3">Posts réseaux sociaux</h5>
                    <div className="space-y-3">
                      <div className="bg-black/30 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <Twitter className="w-4 h-4 text-blue-400" />
                          <span className="text-sm text-gray-400">Twitter/X</span>
                          <button onClick={() => copyToClipboard(shameContent.content.social.twitter)} className="ml-auto">
                            <Copy className="w-3 h-3 text-gray-400" />
                          </button>
                        </div>
                        <p className="text-white text-xs">{shameContent.content.social.twitter}</p>
                      </div>
                      <div className="bg-black/30 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <Linkedin className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-gray-400">LinkedIn</span>
                          <button onClick={() => copyToClipboard(shameContent.content.social.linkedin)} className="ml-auto">
                            <Copy className="w-3 h-3 text-gray-400" />
                          </button>
                        </div>
                        <p className="text-white text-xs">{shameContent.content.social.linkedin?.slice(0, 200)}...</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Article */}
                {shameContent.content?.article && (
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-orange-400 font-medium flex items-center gap-2">
                        <Newspaper className="w-4 h-4" />
                        Article site IFRAP
                      </h5>
                      <button onClick={() => copyToClipboard(shameContent.content.article.corps)}>
                        <Copy className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                    <p className="text-white font-medium mb-1">{shameContent.content.article.title}</p>
                    <p className="text-gray-300 text-sm italic">{shameContent.content.article.chapeau}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono text-[#00FF88]">Demande de Données</h1>
          <p className="text-gray-400 text-sm mt-1">Sollicitation des administrations publiques</p>
        </div>
        <button
          onClick={fetchRequests}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-400' :
          'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="float-right">×</button>
        </div>
      )}

      {/* Tabs */}
      {renderTabs()}

      {/* Contenu */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
        {activeTab === 'requests' && renderRequestsList()}
        {activeTab === 'scan' && renderScanner()}
        {activeTab === 'new' && renderNewRequest()}
      </div>

      {/* Modal détail */}
      {renderRequestDetail()}
    </div>
  );
};

export default DataRequestPage;
